import React from 'react';
import { Routes, Route } from 'react-router-dom';
import { SpotifyProvider } from './context/SpotifyContext';
import { AppleMusicProvider } from './context/AppleMusicContext';
import { MusicServiceProvider } from './context/MusicServiceContext';
import { ProgressProvider } from './context/ProgressContext';
import { Box, createStandaloneToast } from '@chakra-ui/react';
import Home from './pages/Home';
import Landing from './pages/Landing';
import Player from './pages/Player';
import LyricsTest from './pages/LyricsTest';
import Callback from './pages/Callback';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Settings from './pages/Settings';
import TheatreApps from './pages/TheatreApps';
import GamesPage from './pages/GamesPage';
import GamePage from './pages/GamePage';
import StaticPage from './pages/StaticPage';
import BillingSuccess from './pages/BillingSuccess';
import './App.css';
import { memo } from 'react';
import { useLyricsTheme } from './context/LyricsThemeContext';
import { PrivateRoute } from './components/PrivateRoute';
import { PlayerAccessRoute } from './components/PlayerAccessRoute';
import Pricing from './pages/Pricing';
import { ConsentProvider } from './contexts/ConsentContext';
import CookieConsentBanner from './components/CookieConsentBanner';
import CreatePassword from './pages/CreatePassword';
import ForgotPassword from './pages/ForgotPassword';
import EmailLink from './pages/EmailLink';
import { useAnalytics } from './hooks/useAnalytics';

// Component to apply the right background based on color mode
const BackgroundContainer = memo(({ children }: { children: React.ReactNode }) => {
  const { colorMode } = useLyricsTheme();
  
  return (
    <Box 
      className={colorMode === 'light' ? 'light-mode-bg' : ''} 
      minHeight="100vh"
    >
      {children}
    </Box>
  );
});

BackgroundContainer.displayName = 'BackgroundContainer';

// Create toast for error notifications
const { ToastContainer } = createStandaloneToast();

function App() {
  // Initialize analytics tracking
  useAnalytics();
  
  return (
    <>
      <ConsentProvider>
        <MusicServiceProvider>
          <SpotifyProvider>
            <AppleMusicProvider>
              <ProgressProvider>
                <BackgroundContainer>
                  <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/signup" element={<Signup />} />
                    
                    {/* Public Routes */}
                    <Route path="/theatre-apps" element={<TheatreApps />} />
                    <Route path="/games" element={<GamesPage />} />
                    <Route path="/games/:gameId" element={<GamePage />} />
                    <Route path="/pricing" element={<Pricing />} />
                    <Route path="/billing/success" element={<BillingSuccess />} />
                    <Route path="/create-password" element={<CreatePassword />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/email-link" element={<EmailLink />} />
                    
                    {/* Protected Routes */}
                    <Route 
                      path="/player" 
                      element={
                        <PrivateRoute>
                          <PlayerAccessRoute>
                          <Player />
                          </PlayerAccessRoute>
                        </PrivateRoute>
                      } 
                    />
                    <Route 
                      path="/settings" 
                      element={
                        <PrivateRoute>
                          <Settings />
                        </PrivateRoute>
                      } 
                    />
                    
                    <Route path="/auth/callback" element={<Callback />} />
                    <Route path="/terms" element={<StaticPage page="terms" />} />
                    <Route path="/privacy" element={<StaticPage page="privacy" />} />
                    <Route path="/refund" element={<StaticPage page="refund" />} />
                  </Routes>
                </BackgroundContainer>
                <CookieConsentBanner />
                <ToastContainer />
              </ProgressProvider>
            </AppleMusicProvider>
          </SpotifyProvider>
        </MusicServiceProvider>
      </ConsentProvider>
    </>
  );
}

export default App;
